export const execCadenceErrorCodes = [
  'internal_error',
  'loop_detected',
  'wrong_contact',
  'no_contact',
  'no_cadence',
  'check_responses_error',
  'step_execution_error',
  'linkedin_404',
  'salesNav_404',
  'unknown_page',
  'unknown_error',
] as const;
export type ExecCadenceErrorCode = (typeof execCadenceErrorCodes)[number];

export const execStepErrorCodes = [
  'unresolved_variables',
  'connection_too_long',
  'no_email',
  'no_subject',
  'no_body',
  'get_last_message_error',
  'send_message_error',
  'send_inmail_error',
  'send_follow_error',
  'send_connect_error',
  'send_connection_error',
  'google_auth_error',
  'sending_duplicate_message',
  'sending_recent_duplicate_message',
  'unknown_step_type',
  'other_error',
  'page_cant_send_connection',
  'page_cant_send_inmail',
  'page_cant_get_last_inmail',
  'page_cant_send_message',
  'page_cant_send_follow',
  'page_cant_get_last_message',
  'connection_accept_is_pending',
  'cant_connect',
] as const;

export type ExecStepErrorCode = (typeof execStepErrorCodes)[number];

export class CadenceExecutionUtils {
  static cadenceErrorMessageFromCode(code: ExecCadenceErrorCode) {
    switch (code) {
      case 'check_responses_error': {
        return 'could not check message responses';
      }
      case 'step_execution_error': {
        return 'cadence execution crashed';
      }
      case 'unknown_page': {
        return 'could not run on page';
      }
      case 'unknown_error': {
        return 'could not run cadence';
      }
      case 'loop_detected': {
        return 'stuck in a loop';
      }
      case 'wrong_contact': {
        return 'found profile does not match';
      }
      case 'no_cadence': {
        return 'could not extract data';
      }
      case 'no_contact': {
        return 'could not extract data';
      }
      case 'internal_error': {
        return 'failed to initialize';
      }
      case 'linkedin_404': {
        return 'linkedin page is a 404';
      }
      case 'salesNav_404': {
        return 'sales nav page is a 404';
      }
      default: {
        // ts will complain when non exhaustive
        const unbound: never = code;
        console.error('unknown cadence error code', unbound);
        throw new Error(`unknown error code`);
      }
    }
  }

  static stepErrorMessageFromCode(code: ExecStepErrorCode) {
    switch (code) {
      case 'unresolved_variables': {
        return `could not send, variable has no value`;
      }
      case 'connection_too_long': {
        return `could not send connection request, too long, maximum 300 characters`;
      }
      case 'no_email': {
        return `prospect does not have an email`;
      }
      case 'no_subject': {
        return `no subject provided`;
      }
      case 'no_body': {
        return `no body provided`;
      }
      case 'get_last_message_error': {
        return `could not find last message in the conversation`;
      }
      case 'send_message_error': {
        return `could not send message`;
      }
      case 'send_inmail_error': {
        return `could not send inmail`;
      }
      case 'send_follow_error': {
        return `could not follow`;
      }
      case 'send_connect_error': {
        return `could not send connection request`;
      }
      case 'send_connection_error': {
        return `could not send connection request`;
      }
      case 'google_auth_error': {
        return `could not authenticate with gmail`;
      }
      case 'sending_duplicate_message': {
        return `could not send, tried sending duplicate message`;
      }
      case 'sending_recent_duplicate_message': {
        return `could not send, tried sending duplicate message`;
      }
      case 'unknown_step_type': {
        return `can't run unknown step`;
      }
      case 'other_error': {
        return `could not execute step`;
      }
      case 'page_cant_send_connection': {
        return `this page can't send connection requests`;
      }
      case 'page_cant_send_inmail': {
        return `this page can't send inmails`;
      }
      case 'page_cant_get_last_inmail': {
        return `this page can't get last inmail`;
      }
      case 'page_cant_send_message': {
        return `this page can't send messages`;
      }
      case 'page_cant_send_follow': {
        return `this page can't send follow`;
      }
      case 'page_cant_get_last_message': {
        return `this page can't get last messages`;
      }
      case 'connection_accept_is_pending': {
        return `received connection requests, please accept it first`;
      }
      case 'cant_connect': {
        return `can't connect to prospect`;
      }
      default: {
        // for now untill I get the error codes under control
        return `unknown error`;

        // exhaustive check, for compiler
        // const exhaustiveCheck: never = code;
        // console.error('stepErrorMessageFromCode unhandled case', exhaustiveCheck);
        // throw new Error(`Unhandled case`);
      }
    }
  }
}

/**
 * The campaign-level interrupt message recorded when a sent email bounces (the rep's mailbox synced
 * a mailer-daemon / "Undeliverable:" reply). The address varies per bounce, so the UI matches on the
 * prefix via `isEmailBounceInterruptMessage` rather than on the whole string.
 */
export const EMAIL_BOUNCE_INTERRUPT_PREFIX = "Your email wasn't delivered to ";

export function emailBounceInterruptMessage(address: string): string {
  return `${EMAIL_BOUNCE_INTERRUPT_PREFIX}${address} because the address couldn't be found, or is unable to receive mail.`;
}

export function isEmailBounceInterruptMessage(message: string | null | undefined): boolean {
  return message != null && message.startsWith(EMAIL_BOUNCE_INTERRUPT_PREFIX);
}
